import _toArray from "@babel/runtime/helpers/toArray";
import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import cn from 'classnames';
import { usePopper } from 'react-popper';
import { motion, useAnimation } from 'framer-motion';
import { isArray, unionBy } from 'lodash-es';
var warning = require('warning');
import { PopupTriggerType } from './popup.types';
import { safeInvoke, ensureElement, elementIsOrContains, isDefined, isPositionVertical, isPositionHorizontal, isFunction } from '../../../library';
import { placementToPosition, positionToPlacement } from './popup-utils';
import { PopoverArrow } from './popup-arrow';
import { Overlay } from '../../overlay';
import { ResizeSensor } from '../../resize-sensor/src/resize-sensor';
export var popupClasses = {
  TARGET: 'popup-target',
  WRAPPER: 'popup-wrapper',
  MAIN: 'popup',
  CONTENT: 'popup__content',
  ARROW: 'popup__pointer',
  BACKDROP: 'popup-backdrop',
  IS_OPEN: '-open',
  HAS_POINTER: '-pointer',
  DISMISS: 'popup-dismiss',
  DISMISS_OVERRIDE: 'popup-dismiss-override'
};
export var Popup = function Popup(props) {
  var _React$useState = React.useState(props.defaultIsOpen),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    internalIsOpen = _React$useState2[0],
    setIsOpen = _React$useState2[1];
  var controls = useAnimation();
  var _React$useState3 = React.useState(null),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    referenceElementRef = _React$useState4[0],
    setReferenceElementRef = _React$useState4[1];
  var _React$useState5 = React.useState(null),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    popupElementRef = _React$useState6[0],
    setPopupElementRef = _React$useState6[1];
  var _React$useState7 = React.useState(null),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    popupArrowElementRef = _React$useState8[0],
    setArrowPopupElementRef = _React$useState8[1];
  var _usePopper = usePopper(referenceElementRef, popupElementRef, {
      placement: positionToPlacement(props.position),
      modifiers: getPopoverModifiers()
    }),
    styles = _usePopper.styles,
    attributes = _usePopper.attributes,
    updatePopper = _usePopper.update,
    popperState = _usePopper.state;
  var isOpen = React.useMemo(function () {
    return props.isDisabled ? false : isDefined(props.isOpen) ? props.isOpen : internalIsOpen;
  }, [props.isOpen, internalIsOpen, props.isDisabled]);
  var _ref = isArray(props.transitionDuration) ? props.transitionDuration : [props.transitionDuration, props.transitionDuration],
    _ref2 = _slicedToArray(_ref, 2),
    transitionEnter = _ref2[0],
    transitionLeave = _ref2[1];
  var _hideTimeout = React.useRef();
  var _openTimeout = React.useRef();
  var _lostFocusOnSamePage = React.useRef(true);
  var _isMouseInTargetOrPopover = React.useRef(false);
  React.useEffect(function () {
    return function () {
      _clearScheduled();
    };
  }, []);
  React.useEffect(function () {
    safeInvoke(props.getReferenceRef, referenceElementRef);
  }, [referenceElementRef]);
  React.useEffect(function () {
    safeInvoke(props.getPopupRef, popupElementRef);
  }, [popupElementRef]);
  React.useEffect(function () {
    var placement = popperState === null || popperState === void 0 ? void 0 : popperState.placement;
    if (placement !== undefined) {
      if (isOpen) {
        var animateInitial = props.animateInitial ? isFunction(props.animateInitial) ? props.animateInitial(placement) : props.animateInitial : _objectSpread({
          opacity: 0
        }, getDefaultAnimationPosition(placement));
        var animateOpen = props.animateOpen ? isFunction(props.animateOpen) ? props.animateOpen(placement) : props.animateOpen : {
          transition: {
            duration: transitionEnter / 1000
          },
          x: 0,
          y: 0,
          opacity: 1
        };
        controls.set(animateInitial);
        controls.start(animateOpen);
      } else {
        var animateClose = props.animateClose ? isFunction(props.animateClose) ? props.animateClose(placement) : props.animateClose : _objectSpread({
          transition: {
            duration: transitionLeave / 1000
          },
          opacity: 0
        }, getDefaultAnimationPosition(placement));
        controls.start(animateClose);
      }
    }
    if (!isOpen) {}
  }, [isOpen, popperState]);
  function getIsArrowEnabled() {
    var isMinimal = props.isMinimal;
    return !isMinimal;
  }
  function getDefaultAnimationPosition(placement) {
    var position = placementToPosition(placement);
    var x = position ? isPositionVertical(position) ? position === 'left' ? -8 : 8 : 0 : 0;
    var y = position ? isPositionHorizontal(position) ? position === 'top' ? -8 : 8 : 0 : 0;
    return {
      x: x,
      y: y
    };
  }
  var getIsHoverTriggerKind = function getIsHoverTriggerKind() {
    return props.trigger === PopupTriggerType.HOVER || props.trigger === PopupTriggerType.HOVER_TARGET;
  };
  var getTargetTriggerProps = function getTargetTriggerProps() {
    var trigger = props.trigger;
    var triggers = {};
    switch (trigger) {
      case 'click':
      case 'clickTarget':
        triggers['onClick'] = handleReferenceClick;
        break;
      case 'hover':
      case 'hoverTarget':
        triggers['onMouseOver'] = handleMouseEnter;
        triggers['onMouseLeave'] = handleMouseLeave;
        triggers['onBlur'] = handleBlur;
        triggers['onFocus'] = handleFocus;
        break;
    }
    return triggers;
  };
  var getPopoverTriggerProps = function getPopoverTriggerProps() {
    var triggers = {};
    triggers['onClick'] = handlePopupClick;
    if (props.trigger === PopupTriggerType.HOVER || !props.usePortal && props.trigger === PopupTriggerType.HOVER_TARGET) {
      triggers['onMouseOver'] = handleMouseEnter;
      triggers['onMouseLeave'] = handleMouseLeave;
    }
    return triggers;
  };
  function getPopoverModifiers() {
    var boundary = props.boundary,
      modifiers = props.modifiers;
    return unionBy(modifiers, [{
      name: 'arrow',
      enabled: getIsArrowEnabled(),
      options: {
        padding: 6
      }
    }, {
      name: 'flip',
      options: {
        boundariesElement: boundary
      }
    }, {
      name: 'preventOverflow',
      options: {
        boundariesElement: boundary,
        padding: 12
      }
    }, {
      name: 'offset',
      options: {
        offset: [0, props.isMinimal ? 4 : 8]
      }
    }], 'name');
  }
  function getIsElementInPopup(element) {
    return popupElementRef != null && (popupElementRef === null || popupElementRef === void 0 ? void 0 : popupElementRef.contains(element));
  }
  var _clearScheduled = function _clearScheduled() {
    clearTimeout(_hideTimeout.current);
    clearTimeout(_openTimeout.current);
  };
  var openPopover = function openPopover() {
    var delay = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    var e = arguments.length > 1 ? arguments[1] : undefined;
    _clearScheduled();
    _openTimeout.current = window.setTimeout(function () {
      safeInvoke(props.onVisibilityChange, true, e);
      setIsOpen(true);
    }, delay);
  };
  var closePopover = function closePopover() {
    var delay = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    var e = arguments.length > 1 ? arguments[1] : undefined;
    _clearScheduled();
    _hideTimeout.current = window.setTimeout(function () {
      safeInvoke(props.onVisibilityChange, false, e);
      setIsOpen(false);
    }, delay);
  };
  var handleReferenceClick = function handleReferenceClick(e) {
    if (props.isDisabled) return;
    if (isOpen) {
      closePopover(0, e);
    } else {
      openPopover(0, e);
    }
  };
  var handleReferenceResize = function handleReferenceResize() {
    safeInvoke(updatePopper);
    safeInvoke(props.onResize, popupElementRef);
  };
  var handlePopupClick = function handlePopupClick(e) {
    var eventTarget = e.target;
    var dismissElement = eventTarget.closest(".".concat(popupClasses.DISMISS, ", .").concat(popupClasses.DISMISS_OVERRIDE));
    var shouldDismiss = dismissElement != null && dismissElement.classList.contains(popupClasses.DISMISS);
    var isDisabled = eventTarget.closest(":disabled, .-disabled") != null;
    if (shouldDismiss && !isDisabled && !e.isDefaultPrevented()) {
      closePopover(0, e);
      if (props.captureDismiss) {
        e.preventDefault();
      }
    }
  };
  var handlePopupResize = function handlePopupResize() {
    safeInvoke(updatePopper);
  };
  var handleMouseEnter = function handleMouseEnter(e) {
    _isMouseInTargetOrPopover.current = true;
    if (!props.usePortal && getIsElementInPopup(e.target) && props.trigger === PopupTriggerType.HOVER_TARGET && !props.isOpenedOnFocus) {
      handleMouseLeave(e);
    } else if (!props.isDisabled) {
      openPopover(props.hoverOpenDelay, e);
    }
  };
  var handleMouseLeave = function handleMouseLeave(e) {
    _isMouseInTargetOrPopover.current = false;
    window.setTimeout(function () {
      if (_isMouseInTargetOrPopover.current) {
        return;
      }
      closePopover(props.hoverCloseDelay, e);
    });
  };
  var handleFocus = function handleFocus(e) {
    if (props.isOpenedOnFocus && getIsHoverTriggerKind() && !props.isDisabled) {
      if (e.relatedTarget == null && !_lostFocusOnSamePage.current) {
        return;
      }
      handleMouseEnter(e);
    }
  };
  var handleBlur = function handleBlur(e) {
    if (props.isOpenedOnFocus && getIsHoverTriggerKind() && !props.isDisabled) {
      if (!getIsElementInPopup(e.relatedTarget)) {
        handleMouseLeave(e);
      }
    }
    _lostFocusOnSamePage.current = e.relatedTarget != null;
  };
  var handleOverlayClose = function handleOverlayClose(e) {
    var eventTarget = e.target;
    if (referenceElementRef === null || !elementIsOrContains(referenceElementRef, eventTarget) || e.nativeEvent instanceof KeyboardEvent) {
      closePopover(0, e);
    }
  };
  var renderPopup = function renderPopup() {
    var popoverClassName = cn(popupClasses.MAIN, popperState ? "-attached--".concat(popperState.placement) : '', _defineProperty({}, popupClasses.HAS_POINTER, !props.isMinimal), props.popupClassName);
    return React.createElement("div", _extends({
      className: cn(popupClasses.WRAPPER, props.className),
      ref: setPopupElementRef,
      style: styles.popper
    }, attributes.popper, getPopoverTriggerProps()), React.createElement(ResizeSensor, {
      onResize: handlePopupResize
    }, React.createElement(motion.div, {
      className: popoverClassName,
      animate: controls
    }, getIsArrowEnabled() ? React.createElement(PopoverArrow, {
      style: styles.arrow,
      ref: setArrowPopupElementRef
    }) : null, props.content)));
  };
  var renderReferenceElement = function renderReferenceElement() {
    var _React$Children$toArr = React.Children.toArray(props.children),
      _React$Children$toArr2 = _toArray(_React$Children$toArr),
      rawReference = _React$Children$toArr2[0],
      rest = _React$Children$toArr2.slice(1);
    var safeReference = ensureElement(rawReference);
    var targetClasses = cn(popupClasses.TARGET, props.referenceClassName, safeReference.props.className, _defineProperty({}, popupClasses.IS_OPEN, internalIsOpen));
    var TagName = props.referenceTagName;
    var clonedTarget = React.cloneElement(safeReference, _objectSpread({
      className: targetClasses,
      ref: setReferenceElementRef
    }, getTargetTriggerProps()));
    return React.createElement(React.Fragment, null, React.createElement(ResizeSensor, {
      onResize: handleReferenceResize
    }, isDefined(TagName) ? React.createElement(TagName, _extends({
      className: targetClasses,
      ref: setReferenceElementRef
    }, getTargetTriggerProps(), {
      children: rawReference
    })) : clonedTarget), rest);
  };
  if (React.Children.count(props.children) > 1) {
    warning(false, "\n        Popover should not contain more than one element. Only the first\n        element will be used as the target.\n      ");
  }
  var isContentEmpty = ensureElement(props.content) == null;
  return React.createElement(React.Fragment, null, renderReferenceElement(), React.createElement(Overlay, {
    autoFocus: props.autoFocus,
    backdropClassName: popupClasses.BACKDROP,
    backdropProps: props.backdropProps,
    className: props.portalClassName,
    enforceFocus: props.enforceFocus,
    hasBackdrop: props.hasBackdrop,
    isBlocking: props.isBlocking,
    isClosedOnEscape: props.isClosedOnEscape,
    isClosedOnOutsideClick: props.trigger === PopupTriggerType.CLICK,
    isOpen: isOpen && !isContentEmpty,
    onClose: handleOverlayClose,
    onClosed: props.onClosed,
    onClosing: props.onClosing,
    onOpened: props.onOpened,
    onOpening: props.onOpening,
    portalContainer: props.portalContainer,
    transitionDuration: props.transitionDuration,
    usePortal: props.usePortal
  }, renderPopup()));
};
Popup.displayName = 'Popup';
Popup.defaultProps = {
  autoFocus: false,
  boundary: 'viewport',
  captureDismiss: false,
  content: null,
  defaultIsOpen: false,
  hasBackdrop: false,
  hoverCloseDelay: 150,
  isBlocking: false,
  isClosedOnEscape: true,
  isDisabled: false,
  isOpenedOnFocus: true,
  isMinimal: false,
  modifiers: [],
  position: 'top',
  transitionDuration: 0,
  trigger: PopupTriggerType.CLICK,
  usePortal: true
};